import React, { useMemo, useState } from 'react';
import Page from 'components/Page';

import { Alert, Button, Card, Descriptions, List, Result } from 'antd';
import { FileProtectOutlined, ShoppingCartOutlined, WarningOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import Drawer from 'components/Drawer';
import { dayjs } from 'shared/utils/date';
import StatusTag from 'components/StatusTag';
import SettingsList from 'components/Settings/SettingsList';
import { LICENSE_SETTINGS, MODULE_LICENSE_SETTINGS, MODULE_LIMIT_SETTING, MODULE_LIMIT_SETTING_LABEL } from 'shared/constants/settings';
import { EDIT_LICENSE } from 'shared/constants/permissions';
import useUser from 'hooks/useUser';
import { LicenseInterval, Module } from 'shared/prisma/types';
import useDataSource from 'hooks/useDataSource';
import { renderDate } from 'components/Table/renderers';
import { FormattedMessage, FormattedRelativeTime } from 'react-intl';
import selectUnit from 'shared/utils/selectUnit';
import { dateFormat } from 'shared/utils/formatter';
import { visitLimitOptions } from 'shared/constants/visitLimits';
import SettingLabel from 'components/Settings/SettingLabel';
import { exceededLicenseLimit, formatLicenseLimit, getDataSourceLicenseTitle, getDataSourceLicenseUniqueTitle, hasLicenseLimit, isLicenseFree, isLicenseTrial, reachedLicenseLimit } from 'shared/utils/license';
import LicenseLimit from 'components/LicenseLimit';
import globalMessages from 'translations/messages';
import { dataSourceModules } from 'shared/constants/license';
import Order from 'components/Order';
import { fullOrderInfo } from 'shared/utils/order';
import ChangeLicense from './ChangeLicense';

const extraFields = `
  customer { id paymentType }
  owner { id email }
  licenses {
    id
    startDate
    endDate        
    renewalDate
    interval
    settings
    license {
      id
      title
      uniqueTitle
      level
    }
    modules {
      id
      module 
      title
      uniqueTitle
      level
    }        
    orderConfig {
      ${fullOrderInfo}
    }
  }        
  activeLicense {
    orderConfig {
      ${fullOrderInfo}
    }
  }
`;

function getUsageStatus(usage) {
  if (exceededLicenseLimit(usage.current, usage.limit)) {
    return 'EXCEEDED_LIMIT';
  }
  if (reachedLicenseLimit(usage.current, usage.limit)) {
    return 'REACHED_LIMIT';
  }
  if (exceededLicenseLimit(usage.estimated, usage.limit)) {
    return 'ESTIMATED_EXCEEDED_LIMIT';
  }
  if (reachedLicenseLimit(usage.estimated, usage.limit)) {
    return 'ESTIMATED_REACHED_LIMIT';
  }
  return 'OK';
}

const usageColumns = [{
  dataIndex: 'setting',
  title: 'Name',
  render: setting => <SettingLabel setting={setting} />,
  defaultSortOrder: 'descend',
}, {
  dataIndex: 'period',
  title: 'Period',
  render: (period, record) => `${dateFormat(record.startDate)} - ${dateFormat(record.endDate)}`,
}, {
  dataIndex: 'limit',
  title: 'Limit',
  type: 'number',
  render: limit => <LicenseLimit limit={limit} />,
}, {
  dataIndex: 'current',
  title: 'Current',
  type: 'number',
}, {
  dataIndex: 'estimated',
  title: 'Estimated',
  type: 'number',
}, {
  dataIndex: 'status',
  title: 'Status',
  render: status => <StatusTag status={status} />,
  align: 'center',
}];

const LegacyLicense = ({ license, isSuperUser }) => {
  let visitLimit = license?.settings.matomoMonthlyVisitLimit;

  if (hasLicenseLimit(visitLimit)) {
    visitLimit = visitLimitOptions.find(o => o.value === visitLimit)?.label;
  } else {
    visitLimit = 'Unlimited';
  }

  return (
    <Descriptions column={1}>
      <Descriptions.Item label="Products">{getDataSourceLicenseTitle(license)}</Descriptions.Item>
      {isSuperUser && <Descriptions.Item label="Product versions">{getDataSourceLicenseUniqueTitle(license)}</Descriptions.Item>}
      <Descriptions.Item label="Visits/month">{visitLimit}</Descriptions.Item>
      <Descriptions.Item label="Dedicated analyst">{license.settings.dedicatedAnalyst ? 'Yes' : 'No'}</Descriptions.Item>
      <Descriptions.Item label="Unlimited heatmaps and recordings">{license.settings.unlimitedHeatmaps ? 'Yes' : 'No'}</Descriptions.Item>
    </Descriptions>
  );
};

const productLicenseDescription = (module, moduleLicense, license, isSuperUser) => {
  if (!moduleLicense) return 'Not included';

  const limits = MODULE_LICENSE_SETTINGS[module].prices.settings
    .filter(setting => setting !== 'visitLimit' || MODULE_LIMIT_SETTING[module] === setting)
    .filter(setting => license.settings[setting] != null)
    .map(setting => `${formatLicenseLimit(license.settings[setting])} ${MODULE_LIMIT_SETTING_LABEL[setting]}`);
  const title = isSuperUser && moduleLicense.uniqueTitle ? `${moduleLicense.title} (${moduleLicense.uniqueTitle})` : moduleLicense.title;
  return [title, ...limits].join(' · ');
};

const LicenseCard = ({ title, license, modules, onShowDetails, onShowOrderDetails, isSuperUser, canChange, onUpgrade }) => {
  const addons = license.modules?.filter(m => m.module === Module.Addon);

  const isTrial = isLicenseTrial(license);

  let subscription = license.interval === LicenseInterval.Monthly ? 'Monthly' : 'Yearly';

  if (isTrial) {
    subscription = <FormattedMessage {...globalMessages.trial} />;
  }

  return (
    <Card
      title={title}
      extra={[
        onShowOrderDetails && <Button key="order-details" onClick={onShowOrderDetails}>License renewal</Button>,
        onShowDetails && <Button key="details" onClick={onShowDetails}>License details</Button>,
      ]}
    >
      <Descriptions column={3}>
        <Descriptions.Item label="Subscription">{subscription}</Descriptions.Item>
        <Descriptions.Item label="Period">{`${dateFormat(license.startDate)} - ${dateFormat(license.endDate)}`}</Descriptions.Item>
        {!isTrial && <Descriptions.Item label="Automatic renewal">{license.renewalDate ? renderDate(license.renewalDate) : 'Off'}</Descriptions.Item>}
      </Descriptions>
      {license.license && <LegacyLicense license={license} isSuperUser={isSuperUser} />}
      {modules?.length > 0 && !license.license && (
        <List
          dataSource={modules}
          renderItem={(module) => {
            const moduleLicense = license.modules?.find(m => m.module === module);
            return (
              <List.Item
                actions={canChange ? [
                  <Button key="upgrade" type="link" onClick={() => onUpgrade(module)}>Upgrade</Button>,
                ] : undefined}
              >
                <List.Item.Meta
                  title={<FormattedMessage {...globalMessages[module]} />}
                  description={productLicenseDescription(module, moduleLicense, license, isSuperUser)}
                />
              </List.Item>
            );
          }}
        />
      )}
      {addons?.length > 0 && (
        <Descriptions title="Addons" column={addons.length}>
          {addons.map(addon => <Descriptions.Item key={addon.id}>{addon.title}</Descriptions.Item>)}
        </Descriptions>
      )}
    </Card>
  );
};

const ManageLicense = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [changeLicense, setChangeLicense] = useState(false);
  const { dataSource, loading, hasDataSourcePermission, refetch } = useDataSource({ extraFields, includeLicenseUsage: true });
  const { isSuperUser } = useUser();

  const canChange = hasDataSourcePermission(EDIT_LICENSE);

  const { activeLicense, currentUsage } = dataSource || {};

  const usage = useMemo(() => currentUsage?.map(p => ({
    ...p,
    status: getUsageStatus(p),
  })), [currentUsage]);

  const actionLimitReached = usage?.some(u => u.setting === 'actionLimit' && reachedLicenseLimit(u.current, u.limit));
  const actionLimitEstimatedReached = !actionLimitReached && usage?.some(u => u.setting === 'actionLimit' && reachedLicenseLimit(u.estimated, u.limit));
  const isFree = isLicenseFree(activeLicense);
  const isTrial = isLicenseTrial(activeLicense);
  const upcomingLicense = dataSource?.licenses.find(l => dayjs(l.startDate).isAfter() && l.id !== activeLicense?.id);
  const isEnding = activeLicense && !upcomingLicense && !activeLicense.renewalDate;

  const actions = dataSource && [
    canChange && (
      <Button type="primary" key="change" icon={<FileProtectOutlined />} onClick={() => setChangeLicense(true)}>
        Change license
      </Button>
    ),
  ];

  return (
    <Page loading={loading}>
      <Page.Header
        title="License & Usage"
        extra={actions}
      />
      {/* {upcomingLicense && (
        <Alert
          message={(
            <>
              Your current license will be replaced by a new license on
              {' '}
              {dateFormat(upcomingLicense.startDate)}
            </>
          )}
          type="info"
          showIcon
        />
      )} */}

      {isEnding && isTrial && (
        <Alert
          message="You are currently on a trial license. Upgrade your license before the trial ends to avoid interruption."
          type="warning"
          showIcon
        />
      )}

      {isEnding && !isTrial && (
        <Alert
          message={(
            <>
              Your current license ends
              {' '}
              <FormattedRelativeTime {...selectUnit(new Date(activeLicense.endDate))} />
            </>
          )}
          type="warning"
          showIcon
        />
      )}

      {activeLicense ? (
        <LicenseCard
          title="Active license"
          license={activeLicense}
          modules={dataSourceModules[dataSource.type]}
          onShowDetails={canChange && (() => setShowDetails(activeLicense))}
          onShowOrderDetails={activeLicense.orderConfig && (() => setShowOrderDetails(activeLicense.orderConfig))}
          isSuperUser={isSuperUser}
          canChange={canChange}
          onUpgrade={setChangeLicense}
        />
      ) : (
        <Card title="Active license">
          <Result
            icon={<WarningOutlined />}
            title="No active license"
            extra={(
              <Button type="primary" icon={<ShoppingCartOutlined />} onClick={() => setChangeLicense(true)}>
                Buy new license
              </Button>
            )}
          />
        </Card>
      )}

      {upcomingLicense && (
        <LicenseCard
          title="Upcoming license"
          license={upcomingLicense}
          modules={dataSourceModules[dataSource.type]}
          onShowDetails={canChange && (() => setShowDetails(upcomingLicense))}
          onShowOrderDetails={upcomingLicense.orderConfig && (() => setShowOrderDetails(upcomingLicense.orderConfig))}
          isSuperUser={isSuperUser}
          canChange={canChange}
          onUpgrade={setChangeLicense}
        />
      )}

      <Card title="Current usage">
        {isFree ? (
          <>
            {actionLimitReached && <Alert style={{ marginBottom: '1em' }} message="You have reached your action limit for the current period. Upgrade your license to continue collecting data." type="error" showIcon />}
            {actionLimitEstimatedReached && <Alert style={{ marginBottom: '1em' }} message="You are estimated to reach your action limit before the current period ends. Upgrade your license to avoid interruption." type="warning" showIcon />}
          </>
        ) : (
          <>
            {actionLimitReached && <Alert style={{ marginBottom: '1em' }} message="You have reached your action limit for the current period and will be charged for any extra usage. Upgrade your license to get the lowest price." type="warning" showIcon />}
            {actionLimitEstimatedReached && <Alert style={{ marginBottom: '1em' }} message="You are estimated to reach your action limit before the current period ends and will be charged for extra usage. Upgrade your license to get the lowest price." type="warning" showIcon />}
          </>
        )}

        <Table
          dataSource={usage}
          columns={usageColumns}
          pagination={false}
        />
      </Card>

      <Drawer
        title="License features"
        open={showDetails}
        onClose={() => setShowDetails(false)}
        footer={false}
      >
        {showDetails && <SettingsList settings={LICENSE_SETTINGS} settingsValues={showDetails.settings} hideEmpty />}
      </Drawer>
      <ChangeLicense open={changeLicense} onCancel={() => setChangeLicense(false)} onChange={refetch} initialModule={changeLicense} />
      <Drawer
        title="License configuration"
        open={!!showOrderDetails}
        onClose={() => setShowOrderDetails(false)}
        footer={false}
      >
        {showOrderDetails && <Order order={showOrderDetails} hidePeriod />}
        {showOrderDetails.stripeInvoiceUrl && (
          <Button type="primary" href={showOrderDetails.stripeInvoiceUrl} target="_blank" rel="noopener noreferrer" style={{ marginTop: '1em' }}>
            View invoice
          </Button>
        )}
      </Drawer>
    </Page>
  );
};

export default ManageLicense;
